import type { Practitioner } from "@medplum/fhirtypes";
import type { DosespotPractitionerRoleType } from "@/lib/dosespot/practitioner-role-type";

export const ProviderDosespotExtension = {
  PractitionerRoleType: "vibrant.providers.dosespot-practitioner-role-type",
  RoleSyncRequired: "vibrant.providers.dosespot-role-sync-required",
  RegistrationStatus: "https://dosespot.com/registration-status",
} as const;

/**
 * Reads a DoseSpot extension directly from the Practitioner resource.
 */
function getDosespotExtension(resource: Practitioner, url: string) {
  return resource.extension?.find((extension) => extension.url === url);
}

/**
 * Reads the current DoseSpot EPCS registration status.
 */
export function getProviderDosespotRegistrationStatus(resource: Practitioner): string | undefined {
  const extension = getDosespotExtension(resource, ProviderDosespotExtension.RegistrationStatus);
  return extension?.valueString ?? extension?.valueCode;
}

/**
 * Reads the persisted Vibrant-side DoseSpot role.
 */
export function getProviderDosespotPractitionerRoleType(
  resource: Practitioner,
): string | undefined {
  const extension = getDosespotExtension(resource, ProviderDosespotExtension.PractitionerRoleType);
  return extension?.valueString ?? extension?.valueCode;
}

/**
 * Reads whether the provider should sync their role on the next DoseSpot enrollment.
 */
export function getProviderDosespotRoleSyncRequired(resource: Practitioner): boolean {
  const extension = getDosespotExtension(resource, ProviderDosespotExtension.RoleSyncRequired);
  return extension?.valueBoolean ?? false;
}

/**
 * Applies a client-side optimistic DoseSpot role extension update.
 */
export function applyProviderDosespotPractitionerRoleType({
  resource,
  roleType,
}: {
  resource: Practitioner;
  roleType: DosespotPractitionerRoleType | null;
}): Practitioner {
  const existingExtensions = resource.extension ?? [];
  const filteredExtensions = existingExtensions.filter(
    (extension) => extension.url !== ProviderDosespotExtension.PractitionerRoleType,
  );

  if (roleType === null) {
    return { ...resource, extension: filteredExtensions };
  }

  return {
    ...resource,
    extension: [
      ...filteredExtensions,
      {
        url: ProviderDosespotExtension.PractitionerRoleType,
        valueString: roleType,
      },
    ],
  };
}
